import { platformApiClient } from './platform-auth.service';

/** Admin Management (Main Super Admin only). */

export interface PlatformAdmin {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  platformRole: string;
  isPrimary: boolean;
  status: string;
  createdAt: string;
  lastLoginAt?: string | null;
}

export interface PlatformInviteTokenUser {
  id: string;
  email: string;
  displayName?: string | null;
}

export interface PlatformInviteToken {
  id: string;
  email: string;
  tokenHint: string;
  status: 'active' | 'used' | 'revoked' | 'expired';
  createdAt: string;
  expiresAt: string;
  usedAt?: string | null;
  createdBy?: PlatformInviteTokenUser | null;
  usedBy?: PlatformInviteTokenUser | null;
}

/** Returned once, at creation: the plaintext token is never shown again. */
export interface CreatedPlatformInviteToken {
  id: string;
  email: string;
  token: string;
  tokenHint: string;
  expiresAt: string;
}

export type PlatformAdminAction = 'activate' | 'deactivate' | 'promote' | 'demote';

const base = '/admin/management';

export const platformAdminManagementService = {
  async listAdmins(): Promise<PlatformAdmin[]> {
    const response = await platformApiClient.get<PlatformAdmin[]>(`${base}/admins`);
    return response.data;
  },

  async updateAdmin(id: string, data: { firstName?: string; lastName?: string }): Promise<void> {
    await platformApiClient.put(`${base}/admins/${id}`, data);
  },

  async adminAction(id: string, action: PlatformAdminAction): Promise<void> {
    await platformApiClient.post(`${base}/admins/${id}/${action}`);
  },

  async deleteAdmin(id: string): Promise<void> {
    await platformApiClient.delete(`${base}/admins/${id}`);
  },

  async listTokens(): Promise<PlatformInviteToken[]> {
    const response = await platformApiClient.get<PlatformInviteToken[]>(`${base}/tokens`);
    return response.data;
  },

  async createToken(email: string, expiresInHours: number): Promise<CreatedPlatformInviteToken> {
    const response = await platformApiClient.post<CreatedPlatformInviteToken>(`${base}/tokens`, {
      email,
      expiresInHours,
    });
    return response.data;
  },

  async regenerateToken(id: string): Promise<CreatedPlatformInviteToken> {
    const response = await platformApiClient.post<CreatedPlatformInviteToken>(
      `${base}/tokens/${id}/regenerate`,
    );
    return response.data;
  },

  async revokeToken(id: string): Promise<void> {
    await platformApiClient.post(`${base}/tokens/${id}/revoke`);
  },

  async deleteToken(id: string): Promise<void> {
    await platformApiClient.delete(`${base}/tokens/${id}`);
  },
};
